import { useState, useCallback, useEffect, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { pipe } from 'ramda'

import PlainModal, {
  PlainModalHeader,
  PlainModalContent,
  PlainModalFooter,
  type HandleModalDisplayed,
} from 'app/common/ui-next/plain-modal'

import type { IdentifierType } from 'app/common/constants/identifiers'
import ControlsBar from 'app/common/ui-next/controls-bar'
import PlainButton from 'app/common/ui-next/plain-button'
import format from 'app/common/constants/identifiers/formats'
import * as identifierTypes from 'app/common/constants/identifiers/types'
import PlainHelperText from 'app/common/ui-next/plain-helper-text'

import IdentifierFormItems from './identifier-form-items'
import IdentifierFormEnteringNumber from './identifier-form-entering-number'
import useStatusError from './use-status-error'
import { IdentifierFormBankCard } from './identifier-form-bank-card'
import { IdentifierApproveInfo } from './identifier-approve-info'

import styles from './add-identifier-modal.module.scss'

type Props = {
  accountID?: string | null // используется только если bankCardUseFrame === true
  bankCardUseFrame?: boolean
  children: (toggle: HandleModalDisplayed) => ReactNode
  availableIdentifierTypes?: Array<IdentifierType>
  identifierFormat?: (typeof format)[keyof typeof format]
  createErrorText: (identifierType: IdentifierType | null, identifierCode: string) => string
  onSubmit?: (code: string, identifierType: IdentifierType | null) => Promise<boolean>
  onRefresh?: () => void
  onApprove?: (params: { identifierValue: string }) => Promise<unknown>
  approveInfo: { balance: number; price: number }
}

const AddIdentifierModal = ({
  accountID,
  bankCardUseFrame,
  children,
  availableIdentifierTypes,
  identifierFormat,
  createErrorText,
  onSubmit,
  onRefresh = () => {},
  onApprove,
  approveInfo,
}: Props) => {
  const { t } = useTranslation('identifiers-next')

  const [pending, setPending] = useState(false)
  const [identifierType, setIdentifierType] = useState<IdentifierType | null>(null)
  const [identifierValue, setIdentifierValue] = useState<string>('')
  const { error, resetError } = useStatusError(identifierValue, identifierType, createErrorText)

  const approvalRequired = identifierType === identifierTypes.IDENTIFIER_TINKOFF
  const [approved, setApproved] = useState(false)

  const isSpecialBankCardAddition =
    accountID && bankCardUseFrame && identifierType === identifierTypes.IDENTIFIER_BANK_CARD

  const validValue = !(!identifierValue || /_/.test(identifierValue))
  const valid = validValue && !!identifierType

  const handleSubmit = useCallback(async () => {
    const handleApprove = async () => {
      resetError()
      const res = await onApprove?.({ identifierValue })
      setApproved(!!res)
    }
    const res = await (approvalRequired && !approved
      ? handleApprove()
      : onSubmit?.(identifierValue, identifierType))
    return !!res
  }, [identifierValue, onSubmit, identifierType, onApprove, approvalRequired, approved, resetError])

  const handleDisplayStatusChange = useCallback(
    (display: boolean) => {
      if (!display) {
        resetError()
        setIdentifierValue('')
        setIdentifierType(null)
      }
    },
    [resetError],
  )

  useEffect(() => {
    resetError()
    setApproved(false)
  }, [identifierType, identifierValue]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PlainModal
      formId="addIdentifier"
      className={styles.root}
      controlBy={children}
      onSubmit={handleSubmit}
      onDisplayStatusChange={handleDisplayStatusChange}
      onSubmitStatusChange={setPending}
    >
      <PlainModalHeader>{t('add_identifier_header', 'Добавление идентификатора')}</PlainModalHeader>

      <PlainModalContent maxHeight="40vh">
        <IdentifierFormItems
          availableIdentifierTypes={availableIdentifierTypes}
          currentType={identifierType}
          onChange={setIdentifierType}
        />
      </PlainModalContent>

      {/* frame (ссылка на внеший url) */}
      {isSpecialBankCardAddition && (
        <PlainModalContent unscrollable borderTop paddingFree>
          <IdentifierFormBankCard accountID={accountID} asNewPageLink />
        </PlainModalContent>
      )}

      {/* non-frame */}
      {!isSpecialBankCardAddition && (
        <PlainModalContent unscrollable borderTop>
          <IdentifierFormEnteringNumber
            identifierFormat={identifierFormat}
            identifierValue={identifierValue}
            identifierType={identifierType}
            onChange={setIdentifierValue}
            additionalContent={
              approved ? (
                <IdentifierApproveInfo balance={approveInfo.balance} price={approveInfo.price} />
              ) : (
                <PlainHelperText overflowVisible error>
                  {error}
                </PlainHelperText>
              )
            }
          />
        </PlainModalContent>
      )}

      <PlainModalFooter>
        {(toggleModal) => (
          <ControlsBar right>
            {/* frame  */}

            {identifierType === identifierTypes.IDENTIFIER_BANK_CARD && (
              <PlainButton onClick={pipe(onRefresh, toggleModal)}>
                {t('identifier_entering_bt_close', 'Закрыть')}
              </PlainButton>
            )}

            {/* non-frame */}

            {identifierType !== identifierTypes.IDENTIFIER_BANK_CARD && (
              <PlainButton outline onClick={toggleModal}>
                {t('identifier_entering_bt_cancel', 'Отмена')}
              </PlainButton>
            )}

            {approvalRequired && !approved && (
              <PlainButton type="submit" disabled={!valid || pending}>
                {pending
                  ? t('identifier_entering_bt_tinkoff_check_pending', 'Проверка...')
                  : t('identifier_entering_bt_tinkoff_check', 'Проверить')}
              </PlainButton>
            )}

            {identifierType !== identifierTypes.IDENTIFIER_BANK_CARD &&
              (!approvalRequired || approved) && (
                <PlainButton type="submit" disabled={!valid || pending}>
                  {pending
                    ? t('identifier_entering_bt_submit_submitting', 'Добавление...')
                    : t('identifier_entering_bt_submit', 'Добавить')}
                </PlainButton>
              )}
          </ControlsBar>
        )}
      </PlainModalFooter>
    </PlainModal>
  )
}

export default AddIdentifierModal
